/** @format — Record and transcribe one voice note over HTTP */

import { API_BASE_URL } from "../../config";
import { getToken } from "../../utils/tokenStorage";

const VOICE_NOTE_PATH = "/v1/agents/voice-note";
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_RECORDING_MS = 5 * 60 * 1000;

function preferredMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/webm",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported?.(type)) || "";
}

export function isVoiceNoteSupported() {
  return Boolean(
    typeof window !== "undefined" &&
      typeof window.MediaRecorder !== "undefined" &&
      navigator.mediaDevices?.getUserMedia,
  );
}

export class VoiceNoteError extends Error {
  constructor(message, status = null) {
    super(message);
    this.name = "VoiceNoteError";
    this.status = status;
  }
}

async function transcribeVoiceNote(blob, { chatId, durationMs }) {
  if (!blob?.size) throw new VoiceNoteError("Nothing was recorded.", 400);
  if (blob.size > MAX_BYTES) {
    throw new VoiceNoteError("Voice notes must be smaller than 25 MB.", 413);
  }

  const type = blob.type || "audio/webm";
  const extension = type.includes("mp4") ? "m4a" : type.includes("wav") ? "wav" : "webm";
  const form = new FormData();
  form.append("file", blob, `voice-note.${extension}`);
  if (chatId) form.append("chat_id", chatId);

  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${VOICE_NOTE_PATH}`, {
    method: "POST",
    headers,
    body: form,
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 422) {
      throw new VoiceNoteError("Didn't catch that — try recording again.", 422);
    }
    if (response.status === 502) {
      throw new VoiceNoteError(
        "Voice transcription is temporarily unavailable. Try again or type your message.",
        502,
      );
    }
    const detail = payload?.detail || payload?.message;
    throw new VoiceNoteError(
      typeof detail === "string" ? detail : `Could not transcribe voice note (${response.status}).`,
      response.status,
    );
  }

  const transcript = String(payload?.transcript || "").trim();
  if (!transcript) {
    throw new VoiceNoteError("Didn't catch that — try recording again.", 422);
  }

  return {
    transcript,
    voiceNote: payload?.object_key
      ? {
          object_key: payload.object_key,
          content_type: payload.content_type || type,
          duration_ms: payload.duration_ms ?? durationMs ?? null,
          byte_size: payload.byte_size ?? blob.size,
        }
      : null,
  };
}

/**
 * Starts MediaRecorder immediately. Returns stop/cancel controls.
 * `stop()` uploads and resolves to { transcript, voiceNote }.
 */
export async function startVoiceNoteRecording({ chatId, onAutoStop } = {}) {
  if (!isVoiceNoteSupported()) {
    throw new VoiceNoteError("Voice notes are not supported in this browser.");
  }

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (error) {
    throw new VoiceNoteError(
      error?.name === "NotAllowedError"
        ? "Microphone permission was denied."
        : `Could not open the microphone: ${error?.message || error}`,
    );
  }

  const mimeType = preferredMimeType();
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks = [];
  const startedAt = Date.now();
  let settled = false;
  let autoStopTimer = null;

  const stopTracks = () => stream.getTracks().forEach((track) => track.stop());

  const recording = new Promise((resolve, reject) => {
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data);
    };
    recorder.onerror = (event) => {
      stopTracks();
      reject(new VoiceNoteError(event.error?.message || "Voice recording failed."));
    };
    recorder.onstop = () => {
      stopTracks();
      resolve(
        new Blob(chunks, {
          type: recorder.mimeType || mimeType || "audio/webm",
        }),
      );
    };
  });

  recorder.start(1000);
  autoStopTimer = window.setTimeout(() => {
    if (recorder.state !== "inactive") {
      recorder.stop();
      onAutoStop?.();
    }
  }, MAX_RECORDING_MS);

  const cancel = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(autoStopTimer);
    recorder.ondataavailable = null;
    recorder.onstop = null;
    if (recorder.state !== "inactive") recorder.stop();
    stopTracks();
  };

  const stop = async () => {
    if (settled) throw new VoiceNoteError("This recording has already ended.");
    settled = true;
    window.clearTimeout(autoStopTimer);
    if (recorder.state !== "inactive") recorder.stop();
    const blob = await recording;
    return transcribeVoiceNote(blob, {
      chatId,
      durationMs: Date.now() - startedAt,
    });
  };

  return { stop, cancel, stream };
}
